import { FC, useMemo, useState } from 'react';

import { updateCouponPhoneAndMemo } from '@shared/api/giftCoupon/giftCoupon.controller';
import { toastError, toastSuccess } from '@shared/plugIn/toast';
import { formatPrice } from '@shared/utils/formatPrice';

import { fmtPhone } from './constants';
import { GiftItemEmbed } from './types';
import {
  existingPhoneOf,
  PhoneFormParseResult,
  PhoneFormRowStatus,
  PhoneFormUploadRow,
} from '../../utils/phoneFormExcelUpload';

/**
 * 번호 기입폼 엑셀 업로드 — 미리보기 → 확인 후 일괄 저장 → 결과 표시 → 되돌리기.
 * 번호는 빈 칸 채우기 전용, 메모는 기입 시 변경(수령인 이름 기록).
 * 되돌리기 = 방금 넣은 확인번호 삭제 + 메모는 이전 값 복원.
 */
interface PhoneFormUploadModalProps {
  parsed: PhoneFormParseResult;
  /** 상품명·판매가 표기용 — 주문 스냅샷(giftItemEmbed) 맵 (통합 업로드는 여러 주문 합본) */
  giftItemEmbedMap: Map<string, GiftItemEmbed>;
  /** 통합 업로드에서 쿠폰이 속한 주문 라벨 (couponId → "주문1 (07-21)") — 있으면 주문 열 표시 */
  orderLabelByCouponId?: Map<string, string>;
  /** didApply: 1건이라도 DB에 썼으면 true — 닫을 때 목록 새로고침 필요 */
  onClose: (didApply: boolean) => void;
}

type Phase = 'preview' | 'applying' | 'applied' | 'reverting' | 'reverted';

const statusChip: Record<PhoneFormRowStatus, { label: string; cls: string }> = {
  fill: { label: '번호 저장', cls: 'bg-emerald-100 text-emerald-700' },
  memoOnly: { label: '메모만 변경', cls: 'bg-sky-100 text-sky-700' },
  same: { label: '동일 · 제외', cls: 'bg-gray-100 text-gray-500' },
  blockedExisting: { label: '확인번호 있음 · 제외', cls: 'bg-amber-100 text-amber-700' },
  blockedApplied: { label: '수신 신청됨 · 차단', cls: 'bg-rose-100 text-rose-700' },
  blockedStatus: { label: '상태 제외', cls: 'bg-gray-100 text-gray-500' },
  invalidPhone: { label: '형식 오류', cls: 'bg-orange-100 text-orange-700' },
  unknownCode: { label: '미매칭', cls: 'bg-gray-100 text-gray-500' },
};

export const PhoneFormUploadModal: FC<PhoneFormUploadModalProps> = ({
  parsed,
  giftItemEmbedMap,
  orderLabelByCouponId,
  onClose,
}) => {
  const [phase, setPhase] = useState<Phase>('preview');
  const [progress, setProgress] = useState(0);
  // couponId → 에러 메시지('' = 성공). 적용/되돌리기 각각의 건별 결과.
  const [applyErrors, setApplyErrors] = useState<Map<string, string>>(new Map());
  const [revertErrors, setRevertErrors] = useState<Map<string, string>>(new Map());

  // 저장 대상 — 번호 신규 기입(fill) 또는 메모 변경이 있는 매칭된 행.
  // 번호가 차단된 행이라도 메모 변경은 반영한다.
  const targets = useMemo(
    () =>
      parsed.rows.filter(
        (r) => !!r.coupon && (r.status === 'fill' || !!r.memoChange),
      ),
    [parsed.rows],
  );
  const phoneTargetCount = useMemo(
    () => targets.filter((r) => r.status === 'fill').length,
    [targets],
  );
  const memoTargetCount = useMemo(
    () => targets.filter((r) => !!r.memoChange).length,
    [targets],
  );
  const hasMemoColumn = useMemo(
    () => parsed.rows.some((r) => !!r.memoChange),
    [parsed.rows],
  );
  const counts = useMemo(() => {
    const acc: Partial<Record<PhoneFormRowStatus, number>> = {};
    for (const r of parsed.rows) acc[r.status] = (acc[r.status] ?? 0) + 1;
    return acc;
  }, [parsed.rows]);

  const appliedOk = useMemo(
    () =>
      targets.filter((r) => r.coupon && applyErrors.get(r.coupon._id) === ''),
    [targets, applyErrors],
  );
  const didApply = applyErrors.size > 0;

  /** 행별 저장/되돌리기 DTO — 번호는 fill만, 메모는 memoChange가 있을 때 */
  const applyDtoOf = (r: PhoneFormUploadRow) => {
    const dto: { additionalRecipientPhone?: string; memo?: string } = {};
    if (r.status === 'fill' && r.phone) dto.additionalRecipientPhone = r.phone;
    if (r.memoChange) dto.memo = r.memoChange.to;
    return dto;
  };
  const revertDtoOf = (r: PhoneFormUploadRow) => {
    const dto: { additionalRecipientPhone?: string; memo?: string } = {};
    if (r.status === 'fill' && r.phone) dto.additionalRecipientPhone = '';
    if (r.memoChange) dto.memo = r.memoChange.from;
    return dto;
  };

  const handleApply = async () => {
    setPhase('applying');
    setProgress(0);
    const outcome = new Map<string, string>();
    let done = 0;
    for (const t of targets) {
      if (!t.coupon) continue;
      try {
        await updateCouponPhoneAndMemo(t.coupon._id, applyDtoOf(t));
        outcome.set(t.coupon._id, '');
      } catch (err) {
        outcome.set(
          t.coupon._id,
          err instanceof Error ? err.message : '저장 실패',
        );
      }
      done += 1;
      setProgress(done);
    }
    setApplyErrors(outcome);
    setPhase('applied');
    const ok = Array.from(outcome.values()).filter((v) => v === '').length;
    const failed = outcome.size - ok;
    if (ok) toastSuccess(`확인번호·메모 ${ok}건 저장 완료`);
    if (failed) toastError(`${failed}건 저장 실패 — 아래 목록을 확인해주세요.`);
  };

  // 되돌리기 — 방금 저장 성공한 쿠폰만. 새로 넣은 번호는 삭제, 메모는 이전 값 복원.
  const handleRevert = async () => {
    if (
      !confirm(
        `방금 반영한 ${appliedOk.length}건을 원래대로 되돌립니다.\n(새로 넣은 확인번호 삭제 · 메모는 이전 값 복원)\n계속하시겠습니까?`,
      )
    )
      return;
    setPhase('reverting');
    setProgress(0);
    const outcome = new Map<string, string>();
    let done = 0;
    for (const t of appliedOk) {
      if (!t.coupon) continue;
      try {
        await updateCouponPhoneAndMemo(t.coupon._id, revertDtoOf(t));
        outcome.set(t.coupon._id, '');
      } catch (err) {
        outcome.set(
          t.coupon._id,
          err instanceof Error ? err.message : '되돌리기 실패',
        );
      }
      done += 1;
      setProgress(done);
    }
    setRevertErrors(outcome);
    setPhase('reverted');
    const ok = Array.from(outcome.values()).filter((v) => v === '').length;
    const failed = outcome.size - ok;
    if (ok) toastSuccess(`${ok}건 되돌리기 완료`);
    if (failed) toastError(`${failed}건 되돌리기 실패 — 목록에서 확인해주세요.`);
  };

  const isBusy = phase === 'applying' || phase === 'reverting';
  const busyTotal = phase === 'applying' ? targets.length : appliedOk.length;

  /** 어떤 상품(얼마짜리) 쿠폰인지 — 주문 스냅샷 이름 + 구매 당시 판매가 */
  const itemCell = (r: PhoneFormUploadRow): JSX.Element | string => {
    if (!r.coupon) return '-';
    const embed = giftItemEmbedMap.get(r.coupon.giftItem);
    const price = r.coupon.salePriceRetail ?? embed?.priceRetail;
    if (!embed && price == null) return '-';
    return (
      <>
        {embed?.name ?? '-'}
        {price != null && (
          <span className="whitespace-nowrap text-gray-400">
            {' '}
            · {formatPrice(price)}원
          </span>
        )}
      </>
    );
  };

  /** 행별 결과 셀 — 적용/되돌리기 결과를 한 칸에 요약 (저장 대상 행만 값이 있다) */
  const resultCell = (r: PhoneFormUploadRow): JSX.Element | null => {
    if (!r.coupon || !didApply) return null;
    const applyErr = applyErrors.get(r.coupon._id);
    if (applyErr === undefined) return null;
    if (applyErr)
      return <span className="text-xs text-red-600">저장 실패: {applyErr}</span>;
    const revertErr = revertErrors.get(r.coupon._id);
    if (revertErr === undefined)
      return <span className="text-xs font-medium text-emerald-600">✓ 저장됨</span>;
    if (revertErr)
      return (
        <span className="text-xs text-red-600">되돌리기 실패: {revertErr}</span>
      );
    return <span className="text-xs text-gray-500">↩ 되돌림</span>;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" />
      <div className="relative flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="shrink-0 border-b border-gray-100 px-5 py-3">
          <h3 className="text-sm font-bold text-gray-900">
            번호 기입폼 업로드 — 확인번호 일괄 저장
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            번호는 빈 칸 채우기 전용 — 수신 신청 이력이 있거나 확인번호가 이미
            있는 쿠폰은 자동 제외됩니다. 메모(수령인 이름 등)는 기입 시
            변경되고, 빈 칸이면 기존 메모를 유지합니다.
          </p>
        </div>

        {/* 요약 칩 */}
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 px-5 py-2.5 text-[11px]">
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-700">
            번호 저장 {phoneTargetCount}
          </span>
          {memoTargetCount > 0 && (
            <span className="rounded-full bg-sky-100 px-2 py-0.5 font-semibold text-sky-700">
              메모 변경 {memoTargetCount}
            </span>
          )}
          {(counts.blockedApplied ?? 0) > 0 && (
            <span className="rounded-full bg-rose-100 px-2 py-0.5 text-rose-700">
              수신 신청됨 차단 {counts.blockedApplied}
            </span>
          )}
          {(counts.blockedExisting ?? 0) > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">
              확인번호 있음 제외 {counts.blockedExisting}
            </span>
          )}
          {(counts.same ?? 0) > 0 && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-500">
              동일 {counts.same}
            </span>
          )}
          {(counts.blockedStatus ?? 0) > 0 && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-500">
              상태 제외 {counts.blockedStatus}
            </span>
          )}
          {(counts.invalidPhone ?? 0) > 0 && (
            <span className="rounded-full bg-orange-100 px-2 py-0.5 text-orange-700">
              형식 오류 {counts.invalidPhone}
            </span>
          )}
          {(counts.unknownCode ?? 0) > 0 && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-500">
              미매칭 {counts.unknownCode}
            </span>
          )}
          {parsed.otherOrderSkipped > 0 && (
            <span
              className="rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-600"
              title="통합 기입폼의 다른 주문 쿠폰 행 — 해당 주문에서 같은 파일을 올리면 그쪽 건만 골라 저장됩니다."
            >
              다른 주문 건 {parsed.otherOrderSkipped}행 제외
            </span>
          )}
          {(parsed.skippedUnfilled > 0 || parsed.duplicateDropped > 0) && (
            <span className="text-gray-400">
              (미기입 {parsed.skippedUnfilled}행
              {parsed.duplicateDropped > 0 &&
                ` · 중복 쿠폰번호 ${parsed.duplicateDropped}행`}{' '}
              제외)
            </span>
          )}
        </div>

        {/* 행 목록 */}
        <div className="min-h-0 flex-1 overflow-auto px-5">
          {parsed.rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">
              기입된 행이 없습니다 — 수령번호 칸을 채운 뒤 다시 올려주세요.
            </p>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-[11px] text-gray-500">
                  {orderLabelByCouponId && (
                    <th className="py-1.5 pr-2 font-medium">주문</th>
                  )}
                  <th className="py-1.5 pr-2 font-medium">순번</th>
                  <th className="py-1.5 pr-2 font-medium">쿠폰번호</th>
                  <th className="py-1.5 pr-2 font-medium">상품</th>
                  <th className="py-1.5 pr-2 font-medium">기존 번호</th>
                  <th className="py-1.5 pr-2 font-medium">기입 번호</th>
                  {hasMemoColumn && (
                    <th className="py-1.5 pr-2 font-medium">메모</th>
                  )}
                  <th className="py-1.5 pr-2 font-medium">구분</th>
                  {didApply && <th className="py-1.5 font-medium">결과</th>}
                </tr>
              </thead>
              <tbody>
                {parsed.rows.map((r) => {
                  const chip = statusChip[r.status];
                  const existing = existingPhoneOf(r.coupon);
                  return (
                    <tr
                      key={`${r.code}`}
                      className="border-b border-gray-50 last:border-0"
                    >
                      {orderLabelByCouponId && (
                        <td className="whitespace-nowrap py-1.5 pr-2 text-gray-500">
                          {(r.coupon && orderLabelByCouponId.get(r.coupon._id)) ||
                            '-'}
                        </td>
                      )}
                      <td className="py-1.5 pr-2 text-gray-500">
                        {r.coupon?.seq != null ? `#${r.coupon.seq}` : '-'}
                      </td>
                      <td className="py-1.5 pr-2 font-mono text-gray-700">
                        {r.code}
                      </td>
                      <td className="py-1.5 pr-2 text-gray-700">{itemCell(r)}</td>
                      <td className="py-1.5 pr-2 text-gray-500">
                        {existing ? fmtPhone(existing) : '-'}
                      </td>
                      <td className="py-1.5 pr-2 font-medium text-gray-900">
                        {r.phone
                          ? fmtPhone(r.phone)
                          : r.status === 'memoOnly'
                            ? '-'
                            : r.rawPhone}
                      </td>
                      {hasMemoColumn && (
                        <td className="py-1.5 pr-2 text-gray-700">
                          {r.memoChange ? (
                            r.memoChange.from ? (
                              <>
                                <span className="text-gray-400 line-through">
                                  {r.memoChange.from}
                                </span>{' '}
                                → {r.memoChange.to}
                              </>
                            ) : (
                              r.memoChange.to
                            )
                          ) : (
                            <span className="text-gray-400">
                              {r.coupon?.memo?.trim() || '-'}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="py-1.5 pr-2">
                        <span
                          className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium ${chip.cls}`}
                          title={r.reason || undefined}
                        >
                          {chip.label}
                        </span>
                        {r.reason && r.status !== 'fill' && (
                          <span className="ml-1 text-[10px] text-gray-400">
                            {r.reason}
                          </span>
                        )}
                      </td>
                      {didApply && <td className="py-1.5">{resultCell(r)}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* 하단 액션 */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-100 px-5 py-3">
          <div className="text-xs text-gray-500">
            {isBusy && (
              <span>
                {phase === 'applying' ? '저장 중' : '되돌리는 중'}… {progress}/
                {busyTotal}
              </span>
            )}
            {phase === 'applied' && (
              <span>
                저장 완료 {appliedOk.length}건
                {applyErrors.size - appliedOk.length > 0 &&
                  ` · 실패 ${applyErrors.size - appliedOk.length}건`}{' '}
                — 실수가 있었다면 되돌리기로 방금 저장분을 원복할 수 있어요.
              </span>
            )}
            {phase === 'reverted' && <span>되돌리기 처리 완료.</span>}
          </div>
          <div className="flex items-center gap-2">
            {phase === 'applied' && appliedOk.length > 0 && (
              <button
                type="button"
                onClick={handleRevert}
                className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
              >
                되돌리기 ({appliedOk.length}건 원복)
              </button>
            )}
            {phase === 'preview' && (
              <button
                type="button"
                onClick={handleApply}
                disabled={targets.length === 0}
                className="rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
                title={`번호 ${phoneTargetCount}건 · 메모 ${memoTargetCount}건`}
              >
                {targets.length}건 저장
              </button>
            )}
            <button
              type="button"
              onClick={() => onClose(didApply)}
              disabled={isBusy}
              className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-50"
            >
              {phase === 'preview' ? '취소' : '닫기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
